// Shared palette, number formatting, and ECharts chrome for the CloudMTA home
// screen. Kept apart from the components so the charts and the cards can't
// drift into two different-looking halves of one page.

// INSPINIA's palette (rails/webpack/src/css/app/eui/inspinia/_variables.sass),
// which the rest of the Engine UI is built from.
export const INK = {
  primary: "#444",
  body:    "#676a6c",
  muted:   "#999",

  // The name above a card or chart panel. Picked for its contrast against the
  // card surface rather than by eye: 3.5:1, which sits it back from the figure
  // it labels while staying well clear of the 3:1 floor — and well ahead of the
  // muted grey (2.7:1) the secondary rows use. Changing this means re-checking
  // that ratio, not just nudging the hex.
  title:   "#848688",
  border:  "#e7eaec",
  surface: "#fafafc",
  white:   "#ffffff",
};

// Series colors. These are darkened steps of INSPINIA's navy/yellow/red: the
// theme's own values sit too light to hold their own as chart marks, and the
// three of them do not stay apart under simulated color-vision deficiency.
// This set clears the lightness band, the chroma floor and the CVD separation
// checks. Two of the three still fall short of 3:1 against the card surface,
// so every chart drawn in them also states its figures in text — the legend
// and the labelled totals beside the chart, plus the table view behind the
// toggle.
export const SERIES = {
  accepted: "#18a689",
  deferred: "#d98c28",
  failed:   "#e2434f",

  // Volume is one series with no good/bad reading of its own, so it wears
  // INSPINIA's blue rather than any of the outcome colors.
  volume:   "#1c84c6",

  // The new-messages reference line over the outcome stack. Ink rather than a
  // fourth hue: it is not a fourth outcome to compare against the other three,
  // it is the quantity they are attempts *at*, and drawing it as black on a
  // different mark type keeps that difference in kind visible. It also sits
  // clear of all three outcome colors under any color-vision deficiency.
  messages: "#000000",
};

// The DNS states a domain card breaks its total down by. Verified is the
// resting state, a regression is the one that needs attention today, and
// unverified is work not started.
export const DNS_STATE = {
  verified:     SERIES.accepted,
  regressed:    SERIES.failed,
  not_verified: SERIES.deferred,
};

export function formatCount(value) {
  if (value == null) return "—";

  return Number(value).toLocaleString();
}

// Percentages arrive already rounded to two places, or null when there was
// nothing to take a percentage of.
export function formatRate(value) {
  if (value == null) return "—";

  return `${Number(value).toFixed(2)}%`;
}

// Axis ticks have to stay narrow, so large counts are abbreviated there (and
// only there — every figure the page states in text is written out in full).
export function formatCompact(value) {
  const n = Number(value || 0);

  if (n >= 1e9) return `${(n / 1e9).toFixed(n >= 1e10 ? 0 : 1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(n >= 1e4 ? 0 : 1)}K`;

  return `${n}`;
}

// The chrome every home-screen chart shares: recessive hairline grid, muted
// labels, and a tooltip that reads out the whole hovered column.
//
//   labels    — the category-axis values, in order
//   tooltip   — turns the hovered column's series into the tooltip's markup
//   yAxisName — the unit the y values are in, stood on end beside the axis
export function chartBase({ labels, tooltip, yAxisName }) {
  return {
    animation: false,
    // The plot fills the panel, and the layout then shrinks it by however much
    // room the axis labels and the axis name need, so neither can fall outside
    // the panel's fixed height. This pair is ECharts 6's spelling of the older
    // `containLabel: true`, which 6 ignores unless its legacy shim is
    // registered — and an ignored one clips the labels. "all" rather than
    // "axisLabel" because the latter reserves room for the tick labels only,
    // and would let the y-axis name overflow the panel.
    grid: {
      left: 4, right: 8, top: 12, bottom: 4,
      outerBoundsMode: "same",
      outerBoundsContain: "all",
    },
    textStyle: { fontFamily: "open sans, sans-serif" },
    xAxis: {
      type: "category",
      data: labels,
      axisLine:  { lineStyle: { color: INK.border } },
      axisTick:  { show: false },
      axisLabel: { color: INK.muted, fontSize: 11, hideOverlap: true },
    },
    yAxis: {
      type: "value",
      min: 0,
      // Stood on end and centred against the axis, which is how a unit label
      // reads without competing with the tick values beside it. nameGap is
      // measured from the axis line, so it has to clear those ticks; the
      // layout above then reserves room for the whole thing.
      name: yAxisName,
      nameLocation: "middle",
      nameRotate: 90,
      nameGap: 46,
      nameTextStyle: { color: INK.muted, fontSize: 11 },
      axisLine:  { show: false },
      axisTick:  { show: false },
      axisLabel: { color: INK.muted, fontSize: 11, formatter: formatCompact },
      splitLine: { lineStyle: { color: INK.border, type: "solid" } },
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: INK.white,
      borderColor: INK.border,
      borderWidth: 1,
      padding: [ 6, 10 ],
      textStyle: { color: INK.body, fontSize: 12 },
      axisPointer: { type: "shadow" },
      formatter: tooltip,
    },
  };
}

// The rounded data-end of a column. A dense chart gets a smaller radius: on a
// ten-pixel-wide bar a 4px cap is nearly half the width and reads as a blob
// rather than a softened corner.
const CAP_RADIUS       = [ 4, 4, 0, 0 ];
const DENSE_CAP_RADIUS = [ 2, 2, 0, 0 ];

// Marks the one segment that ends a stacked column. Which outcome that is
// differs column by column (an hour with no failures ends on deferrals), so
// the cap has to ride the data rather than the series, or columns would end
// inconsistently.
export const ROUNDED_CAP       = { borderRadius: CAP_RADIUS };
export const DENSE_ROUNDED_CAP = { borderRadius: DENSE_CAP_RADIUS };

// A line series in the house style, for a reference drawn over bars. It carries
// no symbols: at a bucket every hour there are far too many points to mark
// individually, and the tooltip already reads every series at the hovered
// column. z lifts it above the bars it is compared against.
export function lineSeries({ name, color, data }) {
  return {
    name,
    type: "line",
    data,
    showSymbol: false,
    lineStyle: { color, width: 2, cap: "round", join: "round" },
    itemStyle: { color },
    z: 10,
  };
}

// A bar series in the house style.
//
// `continuous` is for a chart with a bucket every hour across days: the columns
// run together into one histogram rather than standing as separate categories.
// That means no category gap, and no separator border either — the border is
// drawn on every side, so on a touching chart it would reappear as a seam
// between neighbours. Sparse charts keep both: room around each column, and a
// surface-colored edge parting the segments of a stack.
export function barSeries({ name, color, data, stack, rounded, continuous }) {
  return {
    name,
    type: "bar",
    stack,
    data,
    barMaxWidth: continuous ? undefined : 24,
    barCategoryGap: continuous ? "0%" : undefined,
    itemStyle: {
      color,
      borderRadius: rounded ? ( continuous ? DENSE_CAP_RADIUS : CAP_RADIUS ) : 0,
      // Stacked segments are separated by surface, not by a stroke: a 1px
      // edge on each of two touching segments reads as the 2px gap between
      // them.
      borderColor: stack && !continuous ? INK.surface : "transparent",
      borderWidth: stack && !continuous ? 1 : 0,
    },
  };
}
